import { PencilIcon } from '@heroicons/react/24/outline';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import type { AssessmentsAccumulationMappingsResponse } from '@/entities/assessments/api/generated/model';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { useAssessmentComparisonStore } from '@/shared/stores/assessment-comparison-store';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Accordion, Button, Skeleton, Text } from '@/shared/ui';
import { AIAccumulationsTable } from '../assessment-table/components/AIAccumulationsTable';
import { DefaultAccumulationsTable } from '../assessment-table/components/DefaultAccumulationsTable';
import { AssessmentComparisonManagement } from './AssessmentComparisonManagement';

const activeKeyEnum = z.enum(['memo', 'diff', 'yearlyDetail']);

type Props = {
  accumulationMappingsData: AssessmentsAccumulationMappingsResponse;
  amountUnit: AmountUnit;
  /** memo が未取得（placeholder表示中）の場合 true */
  isMemoLoading?: boolean;
};

export const AssessmentComparisonAccordion: FC<Props> = ({
  accumulationMappingsData,
  amountUnit,
  isMemoLoading = false,
}) => {
  const { t } = useTranslation();
  const hasUnsavedChanges = useRouteSidePanelStore((state) => state.hasUnsavedChanges);
  const activeKeys = useAssessmentComparisonStore((state) => state.accordionActiveKeys);
  const setActiveKeys = useAssessmentComparisonStore((state) => state.setAccordionActiveKeys);
  const [isMemoEditing, setIsMemoEditing] = useState(false);
  const isMemoOpen = activeKeys.includes(activeKeyEnum.enum.memo);

  return (
    <Accordion
      type="multiple"
      activeKey={activeKeys}
      onActiveKeyChange={setActiveKeys}
      items={[
        {
          key: activeKeyEnum.enum.memo,
          trigger: (
            <div className="flex flex-1 items-center justify-between">
              <Text variant="label">{t('assessment.memo.title')}</Text>
              {!isMemoEditing && !isMemoLoading && isMemoOpen && (
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMemoEditing(true);
                  }}
                >
                  <PencilIcon className="h-4 w-4" />
                  {t('common.actions.edit')}
                </Button>
              )}
            </div>
          ),
          disabled: hasUnsavedChanges,
          content: isMemoLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <AssessmentComparisonManagement
              assessmentsAccumulationId={accumulationMappingsData.assessments_accumulation_id}
              initialMemo={accumulationMappingsData.memo}
              isMemoEditing={isMemoEditing}
              onMemoEditEnd={() => setIsMemoEditing(false)}
            />
          ),
        },
        {
          key: activeKeyEnum.enum.diff,
          trigger: <Text variant="label">{t('assessment.tabs.diff')}</Text>,
          content: (
            <div className="space-y-4 overflow-x-auto">
              <AIAccumulationsTable
                accumulationData={accumulationMappingsData}
                baseFiscalYear={accumulationMappingsData.base_fiscal_year}
                comparedFiscalYear={accumulationMappingsData.compared_fiscal_year}
                amountUnit={amountUnit}
                isSidePanelView={true}
              />
            </div>
          ),
        },
        {
          key: activeKeyEnum.enum.yearlyDetail,
          trigger: <Text variant="label">{t('assessment.tabs.yearly-detail')}</Text>,
          content: (
            <div className="space-y-4 overflow-x-auto">
              <DefaultAccumulationsTable
                accumulationData={accumulationMappingsData}
                baseFiscalYear={accumulationMappingsData.base_fiscal_year}
                comparedFiscalYear={accumulationMappingsData.compared_fiscal_year}
                amountUnit={amountUnit}
                isSidePanelView={true}
              />
            </div>
          ),
        },
      ]}
    />
  );
};
